/**
 * Cross-references section in a rich chat response.
 * Shows related verse references with optional text previews.
 * Deduplicates by target reference. List behind a twister.
 */

import { useState } from 'react';
import type { CrossRef } from '@skjv/common';
import { ChevronRightIcon } from '@components/icons';
import { InlineVerseRef } from './inline-verse-ref';

interface CrossRefsSectionProps {
  crossRefs: CrossRef[];
}

function deduplicateRefs(refs: CrossRef[]): CrossRef[] {
  const seen = new Set<string>();
  return refs.filter((ref) => {
    const key = ref.to.toLowerCase().replace(/\s+/g, '');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function CrossRefsSection({ crossRefs }: CrossRefsSectionProps) {
  const [open, setOpen] = useState(false);
  const unique = deduplicateRefs(crossRefs);

  return (
    <div className="rich-message-section">
      <h4 className="rich-message-section-title">Cross-References</h4>
      {unique.length === 0 ? (
        <p className="cross-ref-none">No cross-references found for this verse.</p>
      ) : (
        <>
          <button className="word-study-toggle" onClick={() => setOpen(!open)}>
            <ChevronRightIcon className={`h-3 w-3 word-study-chevron ${open ? 'word-study-chevron-open' : ''}`} />
            <span>View {unique.length} cross references...</span>
          </button>
          {open && (
            <>
              <span className="cross-ref-arrow">See also</span>
              <ul className="cross-refs-list">
                {unique.map((ref, i) => (
                  <li key={`${ref.to}-${i}`} className="cross-ref-item">
                    <InlineVerseRef reference={ref.to} displayText={ref.to} />
                    {ref.toText && (
                      <p className="cross-ref-text">&ldquo;{ref.toText}&rdquo;</p>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </div>
  );
}
